import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate, useParams } from 'react-router-dom';
import { docsApi } from '../api';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import SearchDialog from '../components/SearchDialog';
import WaveLines from '../components/WaveLines';
import VeritageLogo from '../components/VeritageLogo';
import Dropdown from '../components/Dropdown';

export function useProducts() {
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    docsApi.products().then(setProducts).catch((e) => setError(e.message));
  }, []);
  return { products, error };
}

/** "/" and "/:product" and "/:product/:version" → first page of that version. */
export function RedirectToFirstPage() {
  const { product, version } = useParams();
  const { products, error } = useProducts();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (!products?.length) return;
    const p = products.find((x) => x.id === product) || products[0];
    const v = p.versions.find((x) => x.id === version) || p.versions.find((x) => x.status === 'stable') || p.versions[0];
    docsApi.nav(p.id, v.id).then((nav) => {
      const first = nav[0]?.pages[0];
      setTarget(first ? `/${p.id}/${v.id}/${first.slug}` : `/${p.id}/${v.id}/_empty`);
    }).catch(() => setTarget(`/${p.id}/${v.id}/_empty`));
  }, [products, product, version]);

  if (error) return <div className="docs-fullscreen-msg">Docs are unavailable right now. {error}</div>;
  if (products && !products.length) return <div className="docs-fullscreen-msg">No documentation has been published yet.</div>;
  if (!target) return <div className="docs-fullscreen-msg"><div className="docs-spinner" /></div>;
  return <Navigate to={target} replace />;
}

export default function DocsLayout() {
  const { product, version } = useParams();
  const navigate = useNavigate();
  const { products } = useProducts();
  const [nav, setNav] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    setNav(null);
    docsApi.nav(product, version).then(setNav).catch(() => setNav([]));
  }, [product, version]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearchOpen((o) => !o); }
      else if (e.key === '/' && !/input|textarea|select/i.test(document.activeElement?.tagName)) { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const changeProduct = useCallback((id) => navigate(`/${id}`), [navigate]);
  const changeVersion = useCallback((id) => navigate(`/${product}/${id}`), [navigate, product]);
  const basePath = `/${product}/${version}`;
  const productCfg = products?.find((p) => p.id === product);

  return (
    <div className="docs-app">
      <div className="docs-backdrop" aria-hidden="true">
        <WaveLines className="docs-backdrop-waves" lines={22} amplitude={70} opacity={0.35} />
      </div>

      <TopBar
        products={products}
        product={product}
        version={version}
        onProductChange={changeProduct}
        onVersionChange={changeVersion}
        onSearch={() => setSearchOpen(true)}
        onMenu={() => setMenuOpen(true)}
      />

      <div className="docs-shell">
        <Sidebar
          nav={nav}
          basePath={basePath}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          footer={products?.length > 0 && (
            <div className="docs-sidebar-pickers">
              {products.length > 1 && <Dropdown label="Product" value={product} options={products} onChange={changeProduct} minWidth={180} />}
              {productCfg && <Dropdown label="Version" value={version} options={productCfg.versions} onChange={changeVersion} />}
            </div>
          )}
        />
        <Outlet context={{ nav, product, version, basePath, products }} />
      </div>

      <footer className="docs-footer">
        <div className="docs-footer-inner">
          <VeritageLogo height={26} mono />
          <p>Human-trust in a world of AI. Verify identity with the wallets your customers already have.</p>
          <nav>
            <a href="https://veritage.io">veritage.io</a>
            <a href="https://app.veritage.io">Dashboard</a>
            <a href="https://playground.veritage.io">Playground</a>
            <a href="mailto:info@veritage.io">info@veritage.io</a>
          </nav>
        </div>
      </footer>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} product={product} version={version} />
    </div>
  );
}
